import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SectionHeading } from "@/components/Atoms";
import RequestBoard from "@/components/RequestBoard";
import { useState } from "react";
import { toast } from "sonner";

function StatusBadge({ status }) {
  const colors = {
    pending: "text-amber-300 border-amber-300/30 bg-amber-300/5",
    approved: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5",
    denied: "text-red-400 border-red-400/30 bg-red-400/5",
  };
  return <span className={`font-mono text-[9px] uppercase tracking-[0.25em] px-2 py-0.5 rounded border ${colors[status] || ""}`}>{status}</span>;
}

function AdminPitch() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ title: "", description: "", reference_url: "" });

  const { data: requests } = useQuery({
    queryKey: ["my-requests"],
    queryFn: async () => (await api.get("/requests/mine")).data,
  });

  const createReq = useMutation({
    mutationFn: async () => (await api.post("/requests", form)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-requests"] });
      setForm({ title: "", description: "", reference_url: "" });
      toast.success("Request submitted");
    },
    onError: (e) => toast.error(apiError(e, "Submit failed")),
  });

  return (
    <div>
      <div className="hud-pill rounded-2xl p-6 mb-8">
        <div className="space-y-3">
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Title</Label>
            <Input data-testid="request-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Describe your card</Label>
            <Textarea data-testid="request-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10 min-h-[120px]" placeholder="Imagery, vibe, rarity tier ideas, story…" />
          </div>
          <div>
            <Label className="text-white/70 text-xs uppercase tracking-[0.2em]">Reference URL (optional)</Label>
            <Input data-testid="request-ref-input" value={form.reference_url} onChange={(e) => setForm({ ...form, reference_url: e.target.value })} className="mt-1 bg-[hsl(220_18%_12%)] border-white/10" placeholder="https://…" />
          </div>
          <Button data-testid="request-submit-button" onClick={() => createReq.mutate()} disabled={!form.title || !form.description || createReq.isPending} className="bg-cyan-400 text-black hover:bg-cyan-300">
            {createReq.isPending ? "Submitting…" : "Submit Request"}
          </Button>
        </div>
      </div>

      <h2 className="font-display text-2xl text-white tracking-[0.08em] mb-3">YOUR REQUESTS</h2>
      {(requests || []).length === 0 ? (
        <div className="hud-pill rounded-2xl p-8 text-center text-white/50">No requests submitted yet.</div>
      ) : (
        <div className="space-y-2">
          {requests.map((r) => (
            <div key={r.id} className="hud-pill rounded-xl p-4 flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-display text-lg text-white tracking-[0.06em]">{r.title}</div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-sm text-white/55 mt-1">{r.description}</div>
                {r.admin_note && <div className="mt-2 text-xs text-cyan-300/80 italic">Admin: {r.admin_note}</div>}
              </div>
              <div className="font-mono text-[10px] text-white/40">{new Date(r.created_at).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RequestsPage() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
      <SectionHeading eyebrow="Requests" title="REQUESTS" description="Trade for the cards you want on the player board, or pitch a brand-new card idea to the admin team." />
      <Tabs defaultValue="board" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 mb-6">
          <TabsTrigger value="board" data-testid="requests-tab-board" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-black">Player Board</TabsTrigger>
          <TabsTrigger value="pitch" data-testid="requests-tab-pitch" className="data-[state=active]:bg-cyan-400 data-[state=active]:text-black">Pitch to Admin</TabsTrigger>
        </TabsList>
        <TabsContent value="board"><RequestBoard /></TabsContent>
        <TabsContent value="pitch"><AdminPitch /></TabsContent>
      </Tabs>
    </div>
  );
}
